import React from 'react';

import { MobileTabBar } from '../calendar/MobileTabBar';
import { useV4AccountState } from './useV4AccountState';
import { youBadge as selectYouBadge, type YouBadge } from './accountState';

/**
 * The v4 replacement for the You-tab badge — the flag-ON path for every surface
 * that mounts the bottom tab bar (MobileTabBarMount, YouPage, CalendarPage,
 * GiftGuidesPage). It renders the SAME `MobileTabBar`; only the badge SOURCE
 * changes, from master's notification-count hook (`useYouTabBadge`) to the v4
 * account-state dot semantics.
 *
 * ONE place, not four. accountState's whole thesis (rule 4) is "one account
 * state, every door renders from it" — wiring the badge per-page would recreate
 * exactly the three-places-each-deciding drift it exists to prevent. So the v4
 * badge lives here, and each site branches:
 *
 *   isV4Preview() ? <V4MobileTabBar active={x} /> : <MobileTabBar active={x} youBadge={legacy} />
 *
 * That keeps each site's legacy `useYouTabBadge()` hook AND its legacy render
 * untouched (flag-OFF stays byte-identical — the false branch is today's code
 * verbatim), while the v4 hook is called unconditionally INSIDE this component
 * (hook-rule-safe — no conditional hook calls in any single component).
 *
 * `youBadge(state)` → the `MobileTabBar` `number | boolean` prop:
 *   { kind: 'none' }        → false  (no badge)
 *   { kind: 'dot' }         → true   (bare dot — "save your boards", anon with a board;
 *                                      never clears until she signs in)
 *   { kind: 'count', count} → count  (real notifications — signed in)
 * Signed-out visitors see no alerts, so dot and count can never both apply.
 */

export interface V4MobileTabBarProps {
  active: 'people' | 'calendar' | 'guides' | 'you';
}

/**
 * Discriminated-union → the bar's `number | boolean`. Exhaustive on purpose: a
 * new `YouBadge` kind without a case is a compile error (the missing return),
 * matching accountState's own no-blank-slot discipline.
 */
function badgeProp(b: YouBadge): number | boolean {
  switch (b.kind) {
    case 'count':
      return b.count;
    case 'dot':
      return true;
    case 'none':
      return false;
  }
}

export const V4MobileTabBar: React.FC<V4MobileTabBarProps> = ({ active }) => {
  const { state, ready } = useV4AccountState();
  // Hold no badge until auth + recipients settle, so the dot/count never
  // flashes a wrong value on the way in (the anti-flash discipline,
  // architecture.md). The bar itself always renders — it is navigation.
  const badge = ready ? badgeProp(selectYouBadge(state)) : false;
  return <MobileTabBar active={active} youBadge={badge} />;
};

export default V4MobileTabBar;
